import { useEffect, useRef, useState } from "react";

const STORAGE_KEY = "longbeam.alertSound";
const THRESHOLD_KEY = "longbeam.alertThreshold";

/**
 * Plays a short chime whenever a NEW signal with score >= threshold arrives.
 * State persisted to localStorage.
 * Returns { enabled, setEnabled, threshold, setThreshold }.
 */
export function useAlertSound(signals) {
  const [enabled, _setEnabled] = useState(() => {
    if (typeof window === "undefined") return true;
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === null ? true : v === "1";
  });
  const [threshold, _setThreshold] = useState(() => {
    if (typeof window === "undefined") return 80;
    const v = window.localStorage.getItem(THRESHOLD_KEY);
    return v ? Number(v) : 80;
  });
  const seenIds = useRef(new Set());
  const firstRun = useRef(true);

  const setEnabled = (v) => {
    _setEnabled(v);
    try { window.localStorage.setItem(STORAGE_KEY, v ? "1" : "0"); } catch (_) { /* ignore */ }
  };
  const setThreshold = (v) => {
    _setThreshold(v);
    try { window.localStorage.setItem(THRESHOLD_KEY, String(v)); } catch (_) { /* ignore */ }
  };

  useEffect(() => {
    if (!signals || !Array.isArray(signals)) return;
    if (firstRun.current) {
      // seed with current signal IDs to avoid chime on first mount
      signals.forEach((s) => seenIds.current.add(s.id));
      firstRun.current = false;
      return;
    }
    if (!enabled) {
      signals.forEach((s) => seenIds.current.add(s.id));
      return;
    }
    let shouldPlay = false;
    signals.forEach((s) => {
      if (!seenIds.current.has(s.id)) {
        seenIds.current.add(s.id);
        if (s.score >= threshold) shouldPlay = true;
      }
    });
    if (shouldPlay) playChime(signals[0]?.direction === "short");
  }, [signals, enabled, threshold]);

  return { enabled, setEnabled, threshold, setThreshold };
}

function playChime(isShort = false) {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = isShort ? [520, 380] : [660, 990]; // descending for short, ascending for long
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + i * 0.12);
      gain.gain.exponentialRampToValueAtTime(0.28, now + i * 0.12 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.28);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + i * 0.12);
      osc.stop(now + i * 0.12 + 0.32);
    });
    setTimeout(() => ctx.close(), 900);
  } catch (e) {
    // silent fail — some browsers require user interaction first
    console.warn("chime failed:", e);
  }
}
